import { useEffect, useMemo, useState, Fragment } from "react"
import { useNavigate, useParams } from "react-router-dom"
import type { AppShell } from "@/App"
import {
  listOrders,
  listOrderItems,
  listStockChecks,
  listSupplierOrders,
  listSupplierOrderItems,
  listEticdataDocNumbers,
  realEncNumber,
  resolvePaymentInfo,
  parseLineChanges,
  paymentLabel,
  setLineSeparated,
  invokeAction,
  listAdjustments,
  createAdjustment,
  deleteAdjustment,
  listAppUsers,
  getCurrentUser,
  getAppConfig,
  parseWarehouseOperators,
  authorLabel,
  adjustmentStatusLabel,
  ADJUSTMENT_STATUSES,
  type OrderRow,
  type OrderItem,
  type OrderItemAdjustment,
  type CurrentUser,
  isErpOrder,
  missingCustomerData,
  withExactQty,
} from "@/lib/api"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog"
import { OrderComments } from "@/components/order-comments"
import { OrderChangeAlert } from "@/components/order-change-alert"
import { LineChangeBadge } from "@/components/line-change-badge"
import { SourceBadge } from "@/components/source-badge"
import { useOrderSources } from "@/lib/sources"
import { ArrowLeft, PackageCheck, SplitSquareHorizontal, Store, Trash2 } from "lucide-react"

interface Props {
  shell: AppShell | null
}

/** Linha de picking agregada por SKU (várias order_items → uma linha). */
interface MergedLine {
  key: string
  /** ids das order_items agregadas — o toggle propaga para todas. */
  ids: string[]
  sku: string
  name: string | null
  vendor: string | null
  qty: number
  /** true só quando TODAS as order_items subjacentes estão separadas. */
  separated: boolean
}

export function LogisticaOrder({ shell }: Props) {
  const { id = "" } = useParams()
  const navigate = useNavigate()
  const [order, setOrder] = useState<OrderRow | null>(null)
  const [items, setItems] = useState<OrderItem[]>([])
  const [images, setImages] = useState<Map<string, string>>(new Map())
  const [refBySku, setRefBySku] = useState<Map<string, string>>(new Map())
  const [stockBySku, setStockBySku] = useState<Map<string, { matched: boolean; missing: number }>>(new Map())
  const [supplierBySku, setSupplierBySku] = useState<Map<string, { status: string; brand: string | null }>>(new Map())
  const [encNumber, setEncNumber] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [busyLine, setBusyLine] = useState<string | null>(null)
  // Hover image preview — fixed-positioned so it escapes the table's overflow
  // clipping. Anchored next to the thumbnail, clamped to the viewport.
  const [preview, setPreview] = useState<{ src: string; x: number; y: number } | null>(null)
  const [confirming, setConfirming] = useState(false)
  const [marking, setMarking] = useState(false)
  // Operadores do armazém (setting warehouse_operators) + escolha no popup.
  const [operators, setOperators] = useState<string[]>([])
  // Levantamento em loja: avisar que está pronta / registar o levantamento.
  const [busyPickup, setBusyPickup] = useState<"pickup_ready" | "mark_picked_up" | null>(null)
  const sources = useOrderSources(shell)
  // Cliente já avisado → o 1.º clique só "arma" o reenvio (evita emails repetidos).
  const [resendArmed, setResendArmed] = useState(false)
  const [pickedBy, setPickedBy] = useState<string | null>(null)
  // Ajustes de armazém (informativo — não altera ENC/fatura).
  const [adjustments, setAdjustments] = useState<OrderItemAdjustment[]>([])
  const [users, setUsers] = useState<Map<string, string>>(new Map())
  const [me, setMe] = useState<CurrentUser | null>(null)
  // Dialog de ajuste da linha aberta.
  const [adjLine, setAdjLine] = useState<MergedLine | null>(null)
  const [adjQty, setAdjQty] = useState("1")
  const [adjStatus, setAdjStatus] = useState<string>(ADJUSTMENT_STATUSES[0].value)
  const [adjComment, setAdjComment] = useState("")
  const [adjSaving, setAdjSaving] = useState(false)

  // Config lida uma vez — a lista de operadores não muda durante o polling.
  useEffect(() => {
    if (!shell) return
    getAppConfig(shell)
      .then((cfg) =>
        setOperators(
          parseWarehouseOperators(
            cfg.config.warehouse_operators ?? cfg.schema.warehouse_operators?.default,
          ),
        ),
      )
      .catch(() => setOperators([]))
  }, [shell])

  async function load() {
    if (!shell || !id) return
    try {
      const [orders, its, checks, supOrders, supItems, encMap, adjs, us, cur] = await Promise.all([
        listOrders(shell, 100),
        listOrderItems(shell, id),
        listStockChecks(shell, id),
        listSupplierOrders(shell, id),
        listSupplierOrderItems(shell, id),
        listEticdataDocNumbers(shell),
        listAdjustments(shell, id),
        listAppUsers(shell),
        getCurrentUser(shell),
      ])
      setOrder(orders.find((o) => o.id === id) ?? null)
      // Ordenar por SKU ascendente — MESMO critério da ENC no ETICDATA / PDF.
      setItems([...its].map(withExactQty).sort((a, b) => (a.sku < b.sku ? -1 : 1)))
      setAdjustments(adjs)
      setUsers(us)
      setMe(cur)
      const imgs = new Map<string, string>()
      const refs = new Map<string, string>()
      const stock = new Map<string, { matched: boolean; missing: number }>()
      for (const c of checks) {
        const key = c.sku.trim().toLowerCase()
        if (c.image_url) imgs.set(key, c.image_url)
        if (c.eticdata_ref) refs.set(key, c.eticdata_ref)
        stock.set(key, { matched: c.matched, missing: c.missing_qty ?? 0 })
      }
      // sku → supplier order (status + brand), via supplier_order_items.
      const soById = new Map(supOrders.map((s) => [s.id, s]))
      const sup = new Map<string, { status: string; brand: string | null }>()
      for (const it of supItems) {
        const so = soById.get(it.supplier_order_id)
        if (so) sup.set(it.sku.trim().toLowerCase(), { status: so.status, brand: so.brand })
      }
      setImages(imgs)
      setRefBySku(refs)
      setStockBySku(stock)
      setSupplierBySku(sup)
      setEncNumber(realEncNumber(encMap.get(id) ?? null))
      setError(null)
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err))
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    void load()
  }, [shell, id])

  // Linhas AGREGADAS por SKU — mesmo critério da ENC no ETICDATA
  // (buildEticdataDocPayload soma as quantidades da mesma referência). O
  // Shopify pode mandar o mesmo SKU em várias linhas; o armazém deve ver uma.
  const mergedLines = useMemo(() => {
    const m = new Map<string, MergedLine>()
    for (const it of items) {
      const key = it.sku.trim().toLowerCase()
      const cur = m.get(key)
      if (cur) {
        cur.ids.push(it.id)
        cur.qty += it.qty
        cur.separated = cur.separated && it.separated_at != null
      } else {
        m.set(key, {
          key,
          ids: [it.id],
          sku: it.sku,
          name: it.name,
          vendor: it.vendor,
          qty: it.qty,
          separated: it.separated_at != null,
        })
      }
    }
    return Array.from(m.values())
  }, [items])

  const progress = useMemo(() => {
    const sep = mergedLines.filter((l) => l.separated).length
    return { sep, total: mergedLines.length, done: mergedLines.length > 0 && sep === mergedLines.length }
  }, [mergedLines])

  // Ajustes agrupados por linha + quantidade "OK" derivada (qty − ajustes).
  const adjByLine = useMemo(() => {
    const m = new Map<string, OrderItemAdjustment[]>()
    for (const a of adjustments) {
      const list = m.get(a.order_item_id) ?? []
      list.push(a)
      m.set(a.order_item_id, list)
    }
    return m
  }, [adjustments])

  /** Ajustes de TODAS as order_items por trás da linha agregada. */
  function adjustmentsFor(line: MergedLine): OrderItemAdjustment[] {
    return line.ids.flatMap((id) => adjByLine.get(id) ?? [])
  }

  function adjustedQty(line: MergedLine): number {
    const used = adjustmentsFor(line).reduce((s, a) => s + (Number(a.qty) || 0), 0)
    return Math.max(0, line.qty - used)
  }

  function openAdjust(line: MergedLine) {
    setAdjLine(line)
    setAdjQty(String(Math.max(1, adjustedQty(line) > 0 ? 1 : line.qty)))
    setAdjStatus(ADJUSTMENT_STATUSES[0].value)
    setAdjComment("")
  }

  async function saveAdjust() {
    if (!shell || !adjLine) return
    const qty = Number(adjQty)
    if (!Number.isFinite(qty) || qty <= 0) {
      shell.bridge.toast({ variant: "error", description: "Quantidade inválida." })
      return
    }
    if (qty > adjustedQty(adjLine)) {
      shell.bridge.toast({
        variant: "error",
        description: `Só restam ${adjustedQty(adjLine)} por ajustar nesta linha.`,
      })
      return
    }
    setAdjSaving(true)
    try {
      await createAdjustment(shell, {
        orderId: id,
        orderItemId: adjLine.ids[0],
        qty,
        status: adjStatus,
        comment: adjComment,
        createdByName: me?.name ?? null,
      })
      setAdjLine(null)
      await load()
    } catch (err) {
      shell.bridge.toast({ variant: "error", description: err instanceof Error ? err.message : String(err) })
    } finally {
      setAdjSaving(false)
    }
  }

  async function removeAdjust(adjId: string) {
    if (!shell) return
    try {
      await deleteAdjustment(shell, adjId)
      await load()
    } catch (err) {
      shell.bridge.toast({ variant: "error", description: err instanceof Error ? err.message : String(err) })
    }
  }

  /** Marca/desmarca a linha agregada — propaga para TODAS as order_items que a
   *  compõem (o mesmo SKU pode vir em várias linhas do Shopify). */
  async function toggle(line: MergedLine) {
    if (!shell) return
    const on = !line.separated
    const stamp = on ? new Date().toISOString() : null
    setBusyLine(line.key)
    const ids = new Set(line.ids)
    // Optimistic.
    setItems((prev) =>
      prev.map((it) => (ids.has(it.id) ? { ...it, separated_at: stamp } : it)),
    )
    try {
      await Promise.all(line.ids.map((lineId) => setLineSeparated(shell, lineId, on)))
    } catch (err) {
      // Revert on failure.
      setItems((prev) =>
        prev.map((it) =>
          ids.has(it.id) ? { ...it, separated_at: on ? null : new Date().toISOString() } : it,
        ),
      )
      shell.bridge.toast({ variant: "error", description: err instanceof Error ? err.message : String(err) })
    } finally {
      setBusyLine(null)
    }
  }

  function armResend(notifiedAt: string | null | undefined) {
    setResendArmed(true)
    window.setTimeout(() => setResendArmed(false), 15000)
    shell?.bridge.toast({
      variant: "info",
      description: `Cliente já avisado${notifiedAt ? ` em ${new Date(notifiedAt).toLocaleString("pt-PT", { dateStyle: "short", timeStyle: "short" })}` : ""}. Carrega outra vez para reenviar.`,
    })
  }

  async function runPickup(verb: "pickup_ready" | "mark_picked_up") {
    if (!shell) return
    const force = verb === "pickup_ready" && resendArmed
    if (verb === "pickup_ready" && order?.pickup_notified_at && !force) {
      armResend(order.pickup_notified_at)
      return
    }
    setBusyPickup(verb)
    try {
      const res = await invokeAction<{ customer_notified?: boolean }>(shell, verb, {
        order_id: id,
        ...(force ? { force: true } : {}),
      })
      if (!res.success && res.error?.startsWith("JA_AVISADO|")) {
        armResend(res.error.slice("JA_AVISADO|".length))
        return
      }
      setResendArmed(false)
      if (!res.success) throw new Error(res.error ?? "Falhou")
      shell.bridge.toast({
        variant: "success",
        description:
          verb === "pickup_ready"
            ? res.data?.customer_notified === false
              ? "Gestor avisado. O email ao cliente NÃO saiu — avisar à mão."
              : "Cliente e gestor avisados: pronta para levantamento."
            : "Levantamento registado.",
      })
      await load()
    } catch (err) {
      shell.bridge.toast({ variant: "error", description: err instanceof Error ? err.message : String(err) })
    } finally {
      setBusyPickup(null)
    }
  }

  async function markSeparated() {
    if (!shell) return
    setMarking(true)
    try {
      const res = await invokeAction(shell, "mark_pick_done", {
        order_id: id,
        ...(pickedBy ? { picked_by_name: pickedBy } : {}),
      })
      if (!res.success) throw new Error(res.error ?? "Falhou")
      // Levantamento em loja: separada = pronta no balcão → avisa cliente e gestor de uma vez.
      if (order && resolvePaymentInfo(order).pickupInStore) {
        const ready = await invokeAction<{ customer_notified?: boolean }>(shell, "pickup_ready", { order_id: id })
        shell.bridge.toast({
          variant: ready.success && ready.data?.customer_notified !== false ? "success" : "error",
          description: ready.success
            ? ready.data?.customer_notified === false
              ? "Separada. O email ao cliente NÃO saiu — avisar à mão."
              : "Separada e pronta para levantamento: cliente e gestor avisados."
            : `Separada, mas o aviso de levantamento falhou: ${ready.error ?? "erro"}`,
        })
        await load()
        return
      }
      shell.bridge.toast({ variant: "success", description: "Encomenda marcada como separada." })
      navigate("/logistica")
    } catch (err) {
      shell.bridge.toast({ variant: "error", description: err instanceof Error ? err.message : String(err) })
    } finally {
      setMarking(false)
      setConfirming(false)
    }
  }

  // Per-line status badge for the warehouse.
  function lineStatus(it: MergedLine): { label: string; cls: string } {
    if (it.separated) {
      return { label: "SEPARADO", cls: "border-emerald-500 bg-emerald-50 text-emerald-800" }
    }
    const key = it.key
    const sc = stockBySku.get(key)
    if (sc && sc.matched && sc.missing <= 0) {
      return { label: "EM STOCK", cls: "border-emerald-500 bg-emerald-50 text-emerald-800" }
    }
    // Backorder (out of stock / unmatched). Look at the supplier order state.
    const so = supplierBySku.get(key)
    const brand = (so?.brand || it.vendor || "").trim().toUpperCase()
    // Colour scheme: verde = separado/stock · azul = aguarda marca / aguarda
    // receção · cinza = aguardar encomenda (draft).
    const AZUL = "border-sky-400 bg-sky-50 text-sky-800"
    const CINZA = "border-slate-300 bg-slate-100 text-slate-700"
    if (so) {
      if (so.status === "sent" || so.status === "received" || so.status === "approved") {
        return { label: brand ? `${brand} · aguarda receção` : "Aguarda receção", cls: AZUL }
      }
      // draft / skipped → order not yet sent
      return { label: "AGUARDAR ENCOMENDA", cls: CINZA }
    }
    if (!sc) return { label: "—", cls: "border-border bg-muted text-muted-foreground" }
    return { label: brand ? `AGUARDA ${brand}` : "AGUARDA STOCK", cls: AZUL }
  }

  if (!shell) return <div className="p-6 text-sm text-muted-foreground">Sem token.</div>
  if (loading) return <div className="p-6 text-sm text-muted-foreground">A carregar…</div>
  if (!order) return <div className="p-6 text-sm text-muted-foreground">Encomenda não encontrada.</div>

  const payInfo = resolvePaymentInfo(order)
  // Ainda na separação? (depois de marcada, a encomenda de levantamento fica
  // aqui só para "Reavisar" / "Marcar como levantada").
  const inPicking = order.current_step === "logistica"
  // Última edição no Shopify: o que mudou em cada linha.
  const lineChanges = parseLineChanges(order)
  const removedLines = [...lineChanges.values()].filter((c) => c.after == null)
  const suspendReason = order.cancelled_at
    ? "encomenda cancelada no Shopify."
    : order.payment_pending_at
      ? "editada no Shopify e a aguardar o pagamento da diferença — a lista nova chega quando pagarem."
      : order.shipping_quote_required_at && !order.shipping_quote_paid_at
        ? "aguarda o pagamento dos portes."
        : null
  const shipLine = [order.ship_address, order.ship_zip, order.ship_city, order.ship_country]
    .filter((v) => v != null && String(v).trim() !== "")
    .join(" · ")

  return (
    <div className="flex h-full flex-col">
      <header className="flex items-center gap-3 border-b px-6 py-4">
        <Button variant="outline" size="sm" onClick={() => navigate("/logistica")}>
          <ArrowLeft className="mr-1.5 size-3.5" /> Voltar
        </Button>
        <div className="min-w-0">
          <h1 className="truncate text-lg font-semibold leading-tight">
            Separação · {encNumber ?? order.shopify_order_number ?? order.id.slice(0, 8)}
          </h1>
        </div>
        <div className="ml-auto flex items-center gap-2">
          <SourceBadge name={sources.sourceName(order)} section={sources.sectionOf(order)} />
          <span
            className={
              "inline-flex items-center gap-1 rounded-md border px-2.5 py-1 text-sm font-medium " +
              (progress.done
                ? "border-emerald-500 bg-emerald-50 text-emerald-800"
                : "border-amber-400 bg-amber-50 text-amber-800")
            }
          >
            {progress.done && <PackageCheck className="size-4" />}
            {progress.sep}/{progress.total} separadas
          </span>
        </div>
      </header>

      <div className="flex-1 overflow-auto p-6">
        {error && (
          <Card className="mb-4 border-destructive/30 bg-destructive/5">
            <CardContent className="py-3 text-sm text-destructive">{error}</CardContent>
          </Card>
        )}

        {order && (order.cancelled_at || order.change_alert) && (
          <div className="mb-4">
            <OrderChangeAlert shell={shell} order={order} warehouse />
          </div>
        )}

        {missingCustomerData(order).length > 0 && !order.cancelled_at && (
          <div className="mb-4 rounded-md border border-amber-400 bg-amber-50 px-3 py-2 text-sm text-amber-900">
            <strong>Faltam dados do cliente:</strong> {missingCustomerData(order).join(", ")} — pedir ao comercial para
            preencher no ETICDATA.
          </div>
        )}

        <Card className="mb-4">
          <CardContent className="py-4">
            <div className="text-base font-semibold">
              {isErpOrder(order)
                ? `Encomenda ETICDATA ${order.shopify_order_number ?? "—"}`
                : `Encomenda Online ${order.shopify_order_number ? `#${String(order.shopify_order_number).replace(/^#/, "")}` : "—"}`}
            </div>
            <div className="mt-1 text-sm">{order.ship_name ?? order.customer_name ?? "—"}</div>
            {shipLine && <div className="text-sm text-muted-foreground">{shipLine}</div>}
            {order.ship_phone && (
              <div className="text-sm text-muted-foreground">
                Contacto: <span className="font-medium text-foreground">{order.ship_phone}</span>
              </div>
            )}
            <div className="mt-2 flex flex-wrap items-center gap-2 text-sm">
              <span className="text-muted-foreground">
                Pagamento: <span className="font-medium text-foreground">{paymentLabel(payInfo)}</span>
              </span>
              {payInfo.pickupInStore && (
                <span className="inline-flex items-center rounded-md border border-amber-400 bg-amber-50 px-2 py-0.5 text-[11px] font-bold text-amber-800">
                  LEVANTAMENTO EM LOJA — não expedir
                </span>
              )}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle>Linhas a separar ({items.length})</CardTitle>
          </CardHeader>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-12">Sep.</TableHead>
                <TableHead>Foto</TableHead>
                <TableHead>Referência</TableHead>
                <TableHead>Produto</TableHead>
                <TableHead className="text-right">Qtd</TableHead>
                <TableHead>Estado</TableHead>
                <TableHead className="text-right">Ações</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {mergedLines.map((it) => {
                const sep = it.separated
                const key = it.key
                const img = images.get(key)
                // Composed ETICDATA reference (marca.sku); fall back to raw SKU.
                const ref = refBySku.get(key) ?? it.sku
                const status = lineStatus(it)
                const lineAdjs = adjustmentsFor(it)
                const okQty = adjustedQty(it)
                return (
                  <Fragment key={it.key}>
                  <TableRow
                    className={"cursor-pointer " + (sep ? "bg-emerald-50/40" : "")}
                    onClick={() => void toggle(it)}
                  >
                    <TableCell onClick={(e) => e.stopPropagation()}>
                      <input
                        type="checkbox"
                        className="size-5 accent-emerald-600"
                        checked={sep}
                        disabled={busyLine === it.key}
                        onChange={() => void toggle(it)}
                      />
                    </TableCell>
                    <TableCell onClick={(e) => e.stopPropagation()}>
                      {img ? (
                        <img
                          src={img}
                          alt=""
                          className="size-10 cursor-zoom-in rounded border object-cover"
                          onMouseEnter={(e) => {
                            const r = e.currentTarget.getBoundingClientRect()
                            setPreview({ src: img, x: r.right + 12, y: r.top })
                          }}
                          onMouseLeave={() => setPreview(null)}
                        />
                      ) : (
                        <div className="size-10 rounded border bg-muted" />
                      )}
                    </TableCell>
                    <TableCell className="font-mono text-xs">{ref}</TableCell>
                    <TableCell className={sep ? "text-muted-foreground line-through" : ""}>
                      <div>{it.name ?? "—"}</div>
                      <LineChangeBadge change={lineChanges.get(it.key)} />
                    </TableCell>
                    <TableCell className="text-right tabular-nums">
                      <span className="text-lg font-bold">{okQty}</span>
                      {lineAdjs.length > 0 && (
                        <span className="ml-1 text-xs text-muted-foreground line-through">{it.qty}</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <span
                        className={
                          "inline-flex items-center rounded-md border px-2 py-0.5 text-[11px] font-medium whitespace-nowrap " +
                          status.cls
                        }
                      >
                        {status.label}
                      </span>
                    </TableCell>
                    <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                      <Button
                        variant="outline"
                        size="sm"
                        disabled={okQty <= 0}
                        title={okQty > 0 ? "Ajustar linha (danificado, em falta…)" : "Linha totalmente ajustada"}
                        onClick={() => openAdjust(it)}
                      >
                        <SplitSquareHorizontal className="mr-1.5 size-3.5" />
                        Ajustar
                      </Button>
                    </TableCell>
                  </TableRow>

                  {/* Ajustes de armazém desta linha (informativo). */}
                  {lineAdjs.map((a) => (
                    <TableRow key={a.id} className="bg-amber-50/40">
                      <TableCell />
                      <TableCell />
                      <TableCell className="text-xs text-muted-foreground">ajuste</TableCell>
                      <TableCell className="text-sm">
                        <span className="font-medium">{adjustmentStatusLabel(a.status)}</span>
                        {a.comment && <span className="text-muted-foreground"> · {a.comment}</span>}
                        <div className="text-[11px] text-muted-foreground">
                          {authorLabel(a.created_by_name, a.created_by, users)} ·{" "}
                          {new Date(a.created_at).toLocaleString("pt-PT", {
                            dateStyle: "short",
                            timeStyle: "short",
                          })}
                        </div>
                      </TableCell>
                      <TableCell className="text-right font-bold tabular-nums text-amber-800">
                        {a.qty}
                      </TableCell>
                      <TableCell>
                        <span className="inline-flex items-center rounded-md border border-amber-400 bg-amber-50 px-2 py-0.5 text-[11px] font-medium text-amber-800">
                          {adjustmentStatusLabel(a.status)}
                        </span>
                      </TableCell>
                      <TableCell className="text-right">
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-destructive"
                          title="Remover ajuste"
                          onClick={() => void removeAdjust(a.id)}
                        >
                          <Trash2 className="size-3.5" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                  </Fragment>
                )
              })}
              {removedLines.map((c) => (
                <TableRow key={`removed-${c.sku}`} className="bg-amber-50/40">
                  <TableCell />
                  <TableCell />
                  <TableCell className="font-mono text-xs text-muted-foreground line-through">{c.sku}</TableCell>
                  <TableCell colSpan={4}>
                    <LineChangeBadge change={c} />
                    <div className="text-[11px] text-muted-foreground">Não separar — retirada da encomenda.</div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>

        {/* Marcar completa — à DIREITA. Abre sempre o popup: o operador do
            armazém tem de se identificar; se faltarem linhas mostra o aviso.
            Levantamento em loja: o armazém avisa que está pronta e, depois,
            regista o levantamento — o gestor é notificado dos dois passos. */}
        {suspendReason && (
          <div className="mt-4 rounded-md border border-amber-400 bg-amber-50 px-3 py-2 text-right text-sm font-medium text-amber-900">
            Separação suspensa: {suspendReason}
          </div>
        )}
        <div className="mt-4 flex flex-wrap items-center justify-end gap-2">
          {payInfo.pickupInStore && inPicking === false && !order?.picked_up_at && (
            <Button
              size="lg"
              variant={order?.pickup_ready_at ? "outline" : "default"}
              disabled={busyPickup !== null || !!suspendReason}
              onClick={() => void runPickup("pickup_ready")}
            >
              <Store className="mr-2 size-4" />
              {busyPickup === "pickup_ready"
                ? "A avisar…"
                : resendArmed
                  ? "Confirmar reenvio ao cliente"
                  : order?.pickup_ready_at
                  ? "Reavisar: pronta para levantamento"
                  : "Pronto para levantamento"}
            </Button>
          )}
          {payInfo.pickupInStore && order?.pickup_ready_at && !order?.picked_up_at && (
            <Button
              size="lg"
              disabled={busyPickup !== null}
              onClick={() => void runPickup("mark_picked_up")}
            >
              <PackageCheck className="mr-2 size-4" />
              {busyPickup === "mark_picked_up" ? "A registar…" : "Marcar como levantada"}
            </Button>
          )}
          {order?.picked_up_at && (
            <span className="rounded-md border border-emerald-500 bg-emerald-50 px-2.5 py-1 text-sm font-medium text-emerald-800">
              Levantada em {new Date(order.picked_up_at).toLocaleString("pt-PT", { dateStyle: "short", timeStyle: "short" })}
            </span>
          )}
          {inPicking && (
            <Button
              size="lg"
              // Cancelada / por pagar / aguarda portes: nada a separar.
              disabled={marking || !!suspendReason}
              onClick={() => {
                setPickedBy(null)
                setConfirming(true)
              }}
            >
              {payInfo.pickupInStore ? <Store className="mr-2 size-4" /> : <PackageCheck className="mr-2 size-4" />}
              {marking
                ? "A marcar…"
                : payInfo.pickupInStore
                  ? "Separada — pronta para levantamento"
                  : "Marcar encomenda como COMPLETA"}
            </Button>
          )}
        </div>

        {/* Comentários da encomenda (mesmo fio do detalhe da encomenda). */}
        <div className="mt-4">
          <OrderComments shell={shell} orderId={id} />
        </div>
      </div>

      {/* Confirmação — escolha do operador (obrigatória quando há operadores
          configurados) + aviso das linhas por satisfazer, se houver. */}
      <Dialog open={confirming} onOpenChange={(o) => !o && setConfirming(false)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {payInfo.pickupInStore ? "Separada e pronta para levantamento?" : "Marcar encomenda como completa?"}
            </DialogTitle>
            <DialogDescription>
              {progress.done
                ? payInfo.pickupInStore
                  ? "Todas as linhas estão separadas. O cliente e o gestor são avisados de que a encomenda está pronta para levantamento."
                  : "Todas as linhas estão separadas. A encomenda sai da separação e segue para faturação."
                : "Ainda há linhas por satisfazer. Se avançares, a encomenda sai da separação e segue para faturação."}
            </DialogDescription>
          </DialogHeader>

          {operators.length > 0 && (
            <div className="space-y-2">
              <div className="text-sm font-medium">Quem separou a encomenda?</div>
              <div className="flex flex-wrap gap-2">
                {operators.map((name) => (
                  <Button
                    key={name}
                    type="button"
                    size="lg"
                    variant={pickedBy === name ? "default" : "outline"}
                    aria-pressed={pickedBy === name}
                    disabled={marking}
                    onClick={() => setPickedBy(name)}
                  >
                    {name}
                  </Button>
                ))}
              </div>
            </div>
          )}

          {!progress.done && (
          <div className="rounded-md border border-amber-300 bg-amber-50/60 px-3 py-2 text-sm">
            <div className="font-semibold text-amber-900">
              {progress.total - progress.sep} de {progress.total} linha(s) por separar
            </div>
            <ul className="mt-1.5 space-y-0.5 text-[13px] text-amber-900">
              {mergedLines
                .filter((l) => !l.separated)
                .slice(0, 8)
                .map((l) => (
                  <li key={l.key}>
                    <span className="font-mono text-xs">{l.sku}</span> · {l.name ?? "—"} ({l.qty})
                  </li>
                ))}
            </ul>
          </div>
          )}

          <DialogFooter>
            <Button variant="outline" disabled={marking} onClick={() => setConfirming(false)}>
              Cancelar
            </Button>
            <Button
              disabled={marking || (operators.length > 0 && !pickedBy)}
              onClick={() => void markSeparated()}
            >
              {marking ? "A marcar…" : payInfo.pickupInStore ? "Separada — avisar cliente" : "Marcar como completa"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Ajustar linha — quantidade + estado + comentário (informativo). */}
      <Dialog open={adjLine != null} onOpenChange={(o) => !o && setAdjLine(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Ajustar linha</DialogTitle>
            <DialogDescription>
              {adjLine
                ? `${adjLine.name ?? adjLine.sku} · ${adjustedQty(adjLine)} por separar de ${adjLine.qty}.`
                : ""}{" "}
              Regista o que não vai seguir (danificado, em falta…). Não altera a encomenda nem a
              fatura — fica como registo para quem gere a encomenda.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3">
            <div className="grid grid-cols-[110px_1fr] gap-2">
              <div>
                <label className="mb-1 block text-[11px] text-muted-foreground">Quantidade</label>
                <Input
                  type="number"
                  min={1}
                  max={adjLine ? adjustedQty(adjLine) : 1}
                  value={adjQty}
                  onChange={(e) => setAdjQty(e.target.value)}
                />
              </div>
              <div>
                <label className="mb-1 block text-[11px] text-muted-foreground">Estado</label>
                <select
                  value={adjStatus}
                  onChange={(e) => setAdjStatus(e.target.value)}
                  className="h-9 w-full rounded-md border bg-background px-3 text-sm outline-none focus:ring-2 focus:ring-ring"
                >
                  {ADJUSTMENT_STATUSES.map((s) => (
                    <option key={s.value} value={s.value}>
                      {s.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <div>
              <label className="mb-1 block text-[11px] text-muted-foreground">
                Comentário (ex: "1 dos 2 veio com o vidro partido")
              </label>
              <textarea
                rows={3}
                value={adjComment}
                onChange={(e) => setAdjComment(e.target.value)}
                className="w-full resize-y rounded-md border bg-background px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-ring"
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setAdjLine(null)}>
              Cancelar
            </Button>
            <Button disabled={adjSaving} onClick={() => void saveAdjust()}>
              {adjSaving ? "A guardar…" : "Guardar ajuste"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Hover image preview (fixed → not clipped by the table overflow). */}
      {preview && (
        <div
          className="pointer-events-none fixed z-50 rounded-lg border bg-popover p-1.5 shadow-2xl"
          style={{
            left: Math.min(preview.x, window.innerWidth - 528),
            top: Math.min(Math.max(preview.y - 100, 8), window.innerHeight - 528),
          }}
        >
          <img
            src={preview.src}
            alt=""
            className="h-auto w-[500px] max-w-[80vw] rounded object-contain"
          />
        </div>
      )}
    </div>
  )
}
